import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from './Header';
import Footer from './Footer';

const MindReaderPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const toggleFaq = (index) => setOpenFaq(openFaq === index ? null : index);

  const faqs = [
    {
      question: "Is Simon Nomis a real mind reader?",
      answer: "Simon Nomis is a master mentalist who has spent over 20 years perfecting the art of mind reading and psychological illusion. While the methods remain a mystery, the results are undeniably real—he reveals thoughts, predicts choices, and knows information that seems impossible to know."
    },
    {
      question: "What does a mind reader do during the show?",
      answer: "During Mental Hop Scotch, Simon reads audience members' thoughts, makes predictions about their choices before they make them, reveals personal information, and influences decisions in ways that defy explanation. Every show is unique because it's based on the actual thoughts of that night's audience."
    },
    {
      question: "Will my mind be read?",
      answer: "Possibly! Simon works with many audience members throughout the 90-minute show. While not everyone's mind will be read directly, the entire audience experiences the wonder of watching seemingly impossible feats of mentalism up close."
    },
    {
      question: "Is mind reading real?",
      answer: "The art of mentalism creates the appearance of genuine mind reading through a combination of psychology, observation, suggestion, and closely guarded techniques. Whether it's 'real' is part of the mystery—but the experience certainly feels real to audiences!"
    },
    {
      question: "How is this different from a magic show?",
      answer: "Traditional magic relies on physical props and illusions. Mind reading and mentalism focus on the psychological—reading thoughts, making predictions, and demonstrating apparent psychic abilities. Mental Hop Scotch combines both disciplines for a complete experience."
    },
    {
      question: "Where can I see a mind reader in Orlando?",
      answer: "Simon Nomis performs Mental Hop Scotch every Saturday at 7 PM in Orlando, FL. It's Orlando's premier mind reading and mentalism experience."
    }
  ];

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": "Mind Reader Show Orlando - Mental Hop Scotch",
    "description": "Experience a real mind reader in Orlando. Simon Nomis performs incredible feats of mentalism and mind reading.",
    "url": "https://www.simonnomislive.com/mind-reader",
    "image": "https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/nyl6m05g_mental2.jpg",
    "performer": { "@type": "Person", "name": "Simon Nomis", "jobTitle": "Mind Reader and Mentalist" },
    "location": { "@type": "Place", "name": "Orlando, FL", "address": { "@type": "PostalAddress", "addressLocality": "Orlando", "addressRegion": "FL" } }
  };

  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>Mind Reader Orlando | Simon Nomis | Mental Hop Scotch Live Show</title>
        <meta name="description" content="See a real mind reader in Orlando! Simon Nomis reads thoughts and makes impossible predictions in Mental Hop Scotch. Live mentalism show in Orlando, FL." />
        <meta name="keywords" content="mind reader, mind reader Orlando, mind reading show, mentalist, psychic show Orlando, thought reading, Simon Nomis, mental magic" />
        <link rel="canonical" href="https://www.simonnomislive.com/mind-reader" />
        <meta property="og:title" content="Mind Reader Orlando | Simon Nomis" />
        <meta property="og:description" content="See a real mind reader in Orlando! Simon Nomis reads thoughts and makes impossible predictions." />
        <meta property="og:url" content="https://www.simonnomislive.com/mind-reader" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/nyl6m05g_mental2.jpg" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-black via-purple-900/30 to-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6"><span className="text-purple-400">Mind Reader</span> in Orlando</h1>
            <p className="text-xl md:text-2xl text-purple-300 mb-8">Experience Real Mind Reading Live on Stage</p>
            <p className="text-lg text-gray-300 mb-10 leading-relaxed">
              Have you ever wanted to witness a real <strong className="text-purple-400">mind reader</strong>? Simon Nomis has spent over 20 years mastering the art of reading thoughts, making impossible predictions, and knowing the unknowable. Experience it live at Mental Hop Scotch.
            </p>
            <a href="https://simonnomismagcentertainment.ticketspice.com/mental-hop-scotch" target="_blank" rel="noreferrer" className="inline-block font-bold text-xl text-white rounded-full px-10 py-4 transition-all duration-300 hover:scale-105" style={{ backgroundColor: 'rgb(181, 36, 189)' }}>See the Mind Reader</a>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-10 text-center">What the <span className="text-purple-400">Mind Reader</span> Does</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-purple-900/20 p-6 rounded-lg border border-purple-500/30 text-center">
                <div className="text-4xl mb-4">🧠</div>
                <h3 className="text-lg font-bold text-purple-400 mb-2">Reads Thoughts</h3>
                <p className="text-gray-300 text-sm">Simon reveals what you're thinking before you say it</p>
              </div>
              <div className="bg-purple-900/20 p-6 rounded-lg border border-purple-500/30 text-center">
                <div className="text-4xl mb-4">🔮</div>
                <h3 className="text-lg font-bold text-purple-400 mb-2">Predicts Choices</h3>
                <p className="text-gray-300 text-sm">Makes predictions about decisions before you make them</p>
              </div>
              <div className="bg-purple-900/20 p-6 rounded-lg border border-purple-500/30 text-center">
                <div className="text-4xl mb-4">✨</div>
                <h3 className="text-lg font-bold text-purple-400 mb-2">Reveals Secrets</h3>
                <p className="text-gray-300 text-sm">Knows information that seems impossible to know</p>
              </div>
              <div className="bg-purple-900/20 p-6 rounded-lg border border-purple-500/30 text-center">
                <div className="text-4xl mb-4">🎯</div>
                <h3 className="text-lg font-bold text-purple-400 mb-2">Influences Minds</h3>
                <p className="text-gray-300 text-sm">Subtly guides choices in ways you won't believe</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Simon */}
      <section className="py-16 bg-gradient-to-br from-purple-900/20 via-black to-purple-900/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold text-white mb-4">Meet <span className="text-purple-400">Simon Nomis</span></h2>
                <p className="text-gray-300 mb-4">Simon Nomis is a master mind reader and mentalist with over 20 years of experience. He has dedicated his life to understanding the human mind and creating experiences that blur the line between possible and impossible.</p>
                <p className="text-gray-300 mb-6">His show, Mental Hop Scotch, showcases his incredible abilities in an intimate setting where every audience member gets close to the action.</p>
                <div className="bg-gray-900/50 p-4 rounded-lg border border-purple-500/30">
                  <p className="text-white">📅 Every Saturday at 7:00 PM</p>
                  <p className="text-white">📍 Orlando, FL</p>
                  <p className="text-white">💵 Tickets from $35</p>
                </div>
              </div>
              <img src="https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/nyl6m05g_mental2.jpg" alt="Mind Reader Simon Nomis" className="rounded-lg shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-10 text-center"><span className="text-purple-400">Mind Reading</span> FAQs</h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-900/50 rounded-lg border border-purple-500/30 overflow-hidden">
                  <button onClick={() => toggleFaq(index)} className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-purple-900/20 transition-colors">
                    <span className="text-white font-semibold pr-4">{faq.question}</span>
                    <span className="text-purple-400 text-2xl flex-shrink-0">{openFaq === index ? '−' : '+'}</span>
                  </button>
                  {openFaq === index && <div className="px-6 pb-4"><p className="text-gray-300 leading-relaxed">{faq.answer}</p></div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-purple-900/30 via-black to-purple-900/30">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Have Your <span className="text-purple-400">Mind Read</span></h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">Experience the impossible. See a real mind reader live on stage.</p>
          <a href="https://simonnomismagcentertainment.ticketspice.com/mental-hop-scotch" target="_blank" rel="noreferrer" className="inline-block font-bold text-xl text-white rounded-full px-12 py-5 transition-all duration-300 hover:scale-105" style={{ backgroundColor: 'rgb(181, 36, 189)' }}>Buy Tickets Now</a>
          <p className="text-gray-400 mt-6 text-sm">📍 Orlando, FL | Saturdays at 7PM</p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MindReaderPage;